import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import {
  type ComponentProps,
  type ComponentType,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useStores } from 'src/components/common/root-store-provider/root-store-provider';
import { PaymentMethod } from 'src/models/payments-store/payments-store';
import type { SubscriptionPlan } from 'src/models/subscription-plan/subscription-plan';
import {
  AnalyticsPaymentMethod,
  handleCheckoutOpenRequestWithPaymentSystem,
} from 'src/utils/payment/payment-analytics';
import usePaymentSystem, {
  type PaymentSystem,
} from 'src/utils/payment/use-payment-system';

import { googleAnalyticsEvent, useAnalytics } from '@features/Analytics';
import { createAppLovinSubscriptionPlanPayload } from '@features/Analytics/utils';
import { useFeature } from '@features/FeatureToggle';

import * as auth from '../../../../api/auth';
import ApplePayLogo from '../../../../assets/images/payment/apple-pay-logo.svg';
import PageLoading from '../../../common/page-loading/page-loading';
import page from '../../../pages/page/page';
import PaymentButton from '../payment-button/payment-button';

const SAFARI_AUTH_TOKEN_QUERY_PARAM = 'safari_auth_token';

const useSafariAuthToken = () => {
  const { query } = useRouter();

  const safariAuthTokenQueryValue = query[SAFARI_AUTH_TOKEN_QUERY_PARAM];
  return typeof safariAuthTokenQueryValue === 'string'
    ? safariAuthTokenQueryValue
    : undefined;
};

const IS_APPLE_PAY_AVAILABLE = (() => {
  try {
    return (
      ApplePaySession.supportsVersion(3) && ApplePaySession.canMakePayments()
    );
  } catch {
    return false;
  }
})();

const IS_IN_WEB_VIEW =
  typeof navigator !== 'undefined' &&
  navigator.vendor === 'Apple Computer, Inc.' &&
  /FBA[NV]|Instagram/.test(navigator.userAgent);

export const IS_IN_WEB_VIEW_AND_IOS_LOWER_16 = () => {
  const userAgent = navigator.userAgent;
  // Проверяем, содержит ли userAgent подстроку 'iOS'
  if (userAgent.includes('iOS')) {
    const array = [...userAgent.split('iOS')[1]];

    // Проверяем, что массив не пустой
    if (array.length >= 3) {
      const [first, second, third] = array;
      const version = +`${first}${second}${third}`;

      return version < 16;
    }
  }

  // Если не удается определить версию или не подходит условие, возвращаем false
  return false;
};

export const SHOW_APPLE_PAY_BUTTON =
  (IS_APPLE_PAY_AVAILABLE || IS_IN_WEB_VIEW) &&
  !IS_IN_WEB_VIEW_AND_IOS_LOWER_16();

export const useApplePayPaymentButton = () => {
  const safariAuthToken = useSafariAuthToken();

  const showSafariRedirectPrompt = IS_IN_WEB_VIEW && safariAuthToken != null;
  return { showSafariRedirectPrompt };
};

const paymentSystem: PaymentSystem = 'BRAINTREE';

export const applePayPaymentButtonPage = (
  Component: ComponentType<{ openApplePayCheckoutOnMount: boolean }>,
) =>
  page(
    observer(() => {
      const { authStore } = useStores();
      const { user } = authStore;

      const router = useRouter();
      const { isReady } = router;

      const safariAuthToken = useSafariAuthToken();
      const performLogin =
        !IS_IN_WEB_VIEW && user === null && safariAuthToken != null;

      // biome-ignore lint/correctness/useExhaustiveDependencies: safariAuthToken depends on deps
      useEffect(() => {
        if (!performLogin) {
          return;
        }

        googleAnalyticsEvent({ eventName: 'purchase_open_browser_tapped' });
        (async () => {
          await authStore.loginBySafariAuthToken({
            safariAuthToken: safariAuthToken!,
          });

          const { query, pathname } = router;
          const newQuery = { ...query };
          delete newQuery[SAFARI_AUTH_TOKEN_QUERY_PARAM];
          await router.replace({ pathname, query: newQuery }, undefined, {
            shallow: true,
          });
        })();
      }, [authStore, safariAuthToken, performLogin]);

      const [openCheckoutOnMount, setOpenCheckoutOnMount] = useState(false);
      useEffect(() => {
        if (performLogin) {
          setOpenCheckoutOnMount(true);
        }
      }, [performLogin]);

      return !isReady || performLogin ? (
        <PageLoading />
      ) : (
        <Component openApplePayCheckoutOnMount={openCheckoutOnMount} />
      );
    }),
  );

const ApplePayPaymentButton = observer(
  ({
    subscriptionPlan,
    openCheckoutOnMount,
    onLoadEnd,
    discount = false,
    ...props
  }: {
    subscriptionPlan: SubscriptionPlan;
    openCheckoutOnMount: boolean;
    onLoadEnd: () => void;
    discount?: boolean;
  } & Omit<ComponentProps<typeof PaymentButton>, 'children'>) => {
    const {
      authStore: { variant, googlePseudoID, user },
      paymentsStore,
    } = useStores();
    const { trackFacebookEvent, trackPinterestEvent, trackAppLovin } =
      useAnalytics();
    const fbPixelSept2025Feature = useFeature('fbPixelSept2025Feature');

    const [isLoading, setIsLoading] = useState(false);
    const { contentNode, openCheckout, openCheckoutWithoutGesture } =
      usePaymentSystem({
        subscriptionPlan,
        paymentSystem,
        setIsLoading,
        onLoadEnd,
        braintreeOptions: { useApplePay: true },
      });

    const checkoutOnMountOpened = useRef(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: openCheckoutOnMount depends on deps
    useEffect(() => {
      if (openCheckoutOnMount && !checkoutOnMountOpened.current) {
        // noinspection JSIgnoredPromiseFromCall
        openCheckoutWithoutGesture();
        checkoutOnMountOpened.current = true;
      }
    }, [openCheckoutOnMount, openCheckout, openCheckoutWithoutGesture]);

    const router = useRouter();

    const handleClick = useCallback(async () => {
      if (IS_IN_WEB_VIEW_AND_IOS_LOWER_16()) {
        setIsLoading(true);
        try {
          const safariAuthToken = await auth.getSafariAuthToken();

          const { query, pathname } = router;
          await router.push({
            pathname,
            query: {
              ...query,
              [SAFARI_AUTH_TOKEN_QUERY_PARAM]: safariAuthToken,
              ['variant']: variant,
              ['pseudoId']: googlePseudoID,
            },
          });
        } finally {
          setIsLoading(false);
        }
        return;
      }

      if (paymentSystem != null) {
        handleCheckoutOpenRequestWithPaymentSystem({
          subscriptionPlan,
          paymentMethod: AnalyticsPaymentMethod.APPLE_PAY,
          paymentSystem,
          email: user!.email,
        });
        !fbPixelSept2025Feature &&
          trackFacebookEvent({
            eventName: 'AddPaymentInfo',
            customData: {
              paymentMethod: 'applePay',
              paymentSystem: 'braintree',
            },
            options: {
              email: user?.email,
            },
          });
        trackPinterestEvent({ eventName: 'AddToCart' });
        trackAppLovin({
          eventName: 'add_payment_info',
          options: createAppLovinSubscriptionPlanPayload({
            subscriptionPlan,
            paymentMethod: 'applePay',
            paymentSystem: 'braintree',
            userEmail: user?.email,
            userId: user?.serverId,
            variant,
          }),
        });
      }

      paymentsStore.setPaymentMethod(PaymentMethod.APPLE_PAY);
      await openCheckout();
    }, [
      fbPixelSept2025Feature,
      googlePseudoID,
      openCheckout,
      paymentsStore,
      router,
      subscriptionPlan,
      trackAppLovin,
      trackFacebookEvent,
      trackPinterestEvent,
      user,
      variant,
    ]);

    return (
      <>
        <PaymentButton
          handleClick={handleClick}
          showLoadingIndicator={isLoading}
          {...props}
        >
          {discount ? 'GET MY DISCOUNT' : <ApplePayLogo />}
        </PaymentButton>
        {contentNode}
      </>
    );
  },
);

export default ApplePayPaymentButton;
